<script setup lang="ts">
import type { PartnerPortalContactSessionFragmentFragment } from '@/graphql/pp/generated/graphql';
import {
  sidebarNavigationMenuUi,
  toNavigationMenuItems,
  type SidebarNavItemInput,
} from '@/util/navigationMenu';

const props = defineProps<{
  partner: PartnerPortalContactSessionFragmentFragment,
}>();
const route = useRoute();
const {
  hasLiabilityWaiver,
  canViewLiabilityCategories,
  canViewLiabilityQuestions,
  canViewLiabilityContent,
  canViewLiabilityTemplates,
  canViewLiabilityForms,
  canViewLiabilitySubmissions,
} = usePartner();

const liabilityWaiverBase = computed(() => `/${props.partner.partner.uuid}/liability-waiver`);

const liabilityWaiverRelatedPaths = computed<Record<string, string[]>>(() => ({
  [`${liabilityWaiverBase.value}/categories`]: [`${liabilityWaiverBase.value}/users`],
  [`${liabilityWaiverBase.value}/questions`]: [`${liabilityWaiverBase.value}/lists`],
}));

const matchesPath = (path: string) => (
  route.path === path || route.path.startsWith(`${path}/`)
);

const isWaiverChildActive = (to: string): boolean => {
  const related = liabilityWaiverRelatedPaths.value[to] ?? [];
  return [to, ...related].some(matchesPath);
};

const liabilityWaiverChildren = computed<SidebarNavItemInput[]>(() => {
  const items: SidebarNavItemInput[] = [];
  if (canViewLiabilityCategories.value) {
    items.push({
      faIcon: ['fal', 'layer-group'],
      label: 'Categories',
      to: `${liabilityWaiverBase.value}/categories`,
      value: `${liabilityWaiverBase.value}/categories`,
      active: isWaiverChildActive(`${liabilityWaiverBase.value}/categories`),
    });
  }
  if (canViewLiabilityContent.value) {
    items.push({
      faIcon: ['fal', 'book-open'],
      label: 'Content',
      to: `${liabilityWaiverBase.value}/content`,
      value: `${liabilityWaiverBase.value}/content`,
      active: isWaiverChildActive(`${liabilityWaiverBase.value}/content`),
    });
  }
  if (canViewLiabilityQuestions.value) {
    items.push({
      faIcon: ['fal', 'circle-question'],
      label: 'Questions',
      to: `${liabilityWaiverBase.value}/questions`,
      value: `${liabilityWaiverBase.value}/questions`,
      active: isWaiverChildActive(`${liabilityWaiverBase.value}/questions`),
    });
  }
  if (canViewLiabilityTemplates.value) {
    items.push({
      faIcon: ['fal', 'table-layout'],
      label: 'Templates',
      to: `${liabilityWaiverBase.value}/templates`,
      value: `${liabilityWaiverBase.value}/templates`,
      active: isWaiverChildActive(`${liabilityWaiverBase.value}/templates`),
    });
  }
  if (canViewLiabilityForms.value) {
    items.push({
      faIcon: ['fal', 'file-lines'],
      label: 'Forms',
      to: `${liabilityWaiverBase.value}/forms`,
      value: `${liabilityWaiverBase.value}/forms`,
      active: isWaiverChildActive(`${liabilityWaiverBase.value}/forms`),
    });
  }
  if (canViewLiabilitySubmissions.value) {
    items.push({
      faIcon: ['fal', 'paper-plane'],
      label: 'Submissions',
      to: `${liabilityWaiverBase.value}/submissions`,
      value: `${liabilityWaiverBase.value}/submissions`,
      active: isWaiverChildActive(`${liabilityWaiverBase.value}/submissions`),
    });
  }
  return items;
});

const menuItems = computed(() => {
  if (!hasLiabilityWaiver.value || !liabilityWaiverChildren.value.length) {
    return [];
  }
  return toNavigationMenuItems(
    [
      {
        faIcon: ['fal', 'building-columns'],
        label: 'Liability Waiver',
        value: 'liability-waiver',
        children: liabilityWaiverChildren.value,
      },
    ],
    route.path,
  );
});
</script>

<template>
  <template v-if="menuItems.length">
    <USeparator
      decorative
      class="my-4"
      :ui="{ border: 'border-(--color-sidebar-text)/25' }"
    />
    <LayoutSidebarMenuTitle
      title="CampusCoverage"
      class="mb-3"
    />
    <nav aria-label="CampusCoverage" data-cy="menu-liability-waiver">
      <MenuSidebarNavigationMenu
        :items="menuItems"
        :ui="sidebarNavigationMenuUi"
      />
    </nav>
  </template>
</template>
